import { bioThemes, themeKeys } from '../bioThemes'
import type { WizardState } from './useWizardState'

interface Props {
  state: WizardState
  onChange: (partial: Partial<WizardState>) => void
}

const buttonStyles = [
  { value: 'rounded', label: 'Rounded', radius: '12px' },
  { value: 'square', label: 'Square', radius: '4px' },
  { value: 'pill', label: 'Pill', radius: '50px' },
]

export function WizardStepAppearance({ state, onChange }: Props) {
  function handleThemeSelect(key: string) {
    const theme = bioThemes[key]
    onChange({
      theme: key,
      button_color: theme.buttonBg,
      text_color: theme.textColor,
    })
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Choose your style
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
        Pick a theme and button style for your bio page.
      </p>

      {/* Theme Picker */}
      <section className="mb-8">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Theme</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {themeKeys.map(key => {
            const theme = bioThemes[key]
            const selected = state.theme === key
            const bg = theme.background
            return (
              <button
                key={key}
                onClick={() => handleThemeSelect(key)}
                className={`group relative flex flex-col items-center text-left transition-all ${
                  selected ? 'ring-2 ring-[#2B6CEE] ring-offset-2 dark:ring-offset-[#0d121b]' : 'hover:opacity-90'
                } rounded-xl`}
              >
                <div
                  className="w-full aspect-[3/4] rounded-xl shadow-sm border border-gray-200 dark:border-white/10 mb-2 overflow-hidden relative"
                  style={{ background: bg }}
                >
                  <div className="absolute inset-0 p-3 flex flex-col items-center gap-2 pt-6">
                    <div className="w-8 h-8 rounded-full bg-white/20 mb-1" />
                    <div className="w-16 h-2 rounded-full bg-white/20" />
                    <div className="w-full mt-2 space-y-1.5 px-2">
                      <div className="w-full h-6 rounded-md" style={{
                        background: theme.buttonBg === 'transparent' ? 'transparent' : theme.buttonBg,
                        border: theme.buttonBg === 'transparent' ? `1px solid ${theme.buttonBorder || theme.buttonText}` : 'none'
                      }} />
                      <div className="w-full h-6 rounded-md" style={{
                        background: theme.buttonBg === 'transparent' ? 'transparent' : theme.buttonBg,
                        border: theme.buttonBg === 'transparent' ? `1px solid ${theme.buttonBorder || theme.buttonText}` : 'none',
                        opacity: 0.7
                      }} />
                    </div>
                  </div>
                  {selected && (
                    <div className="absolute inset-0 bg-[#2B6CEE]/10 flex items-center justify-center backdrop-blur-[1px]">
                      <div className="w-8 h-8 bg-[#2B6CEE] rounded-full flex items-center justify-center shadow-lg">
                        <span className="material-symbols-outlined text-white text-[18px]">check</span>
                      </div>
                    </div>
                  )}
                </div>
                <span className={`text-sm font-medium ${selected ? 'text-[#2B6CEE]' : 'text-gray-700 dark:text-gray-300'}`}>
                  {theme.name}
                </span>
              </button>
            )
          })}
        </div>
      </section>

      {/* Button Style */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Button Style</h3>
        <div className="grid grid-cols-3 gap-3 max-w-sm">
          {buttonStyles.map(style => (
            <button
              key={style.value}
              onClick={() => onChange({ button_style: style.value })}
              className={`py-3 text-sm font-medium border transition-all ${
                state.button_style === style.value
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-[#2B6CEE] border-[#2B6CEE]'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5'
              }`}
              style={{ borderRadius: style.radius }}
            >
              {style.label}
            </button>
          ))}
        </div>
      </section>

      <p className="text-xs text-gray-400 dark:text-gray-500 mt-6">
        Customize colors, fonts, and backgrounds in the full editor.
      </p>
    </div>
  )
}
